/**
 * Remotion Image Overlay Component
 *
 * Renders ImageOverlay from Post.ts with pixel-identical output to Fabric.js.
 * Supports objectFit/objectPosition, opacity, rotation, borderRadius, and shadow.
 */

import React from 'react';
import { Img } from 'remotion';
import { ImageOverlay } from '../../models/Post';
import { resolveImageFilterCss } from '../../utils/imageFilter';

interface RemotionImageOverlayProps {
  overlay: ImageOverlay;
  containerWidth: number;
  containerHeight: number;
  /** Pre-signed URL for the image (resolved from filePath) */
  imageUrl: string;
  /** Scale factor: containerWidth / referenceMediaWidth */
  scaleFactor?: number;
  /** Layout engine: 'fabric' (old posts) applies rotation compensation */
  layoutEngine?: 'fabric' | 'remotion';
}

export const RemotionImageOverlay: React.FC<RemotionImageOverlayProps> = ({
  overlay,
  containerWidth,
  containerHeight,
  imageUrl,
  scaleFactor = 1,
  layoutEngine,
}) => {
  const {
    position,
    width,
    height,
    opacity = 100,
    rotation = 0,
    zIndex = 0,
    borderRadius = 0,
    objectFit = 'cover',
    objectPosition,
    shadow,
    sourceColor,
    filter,
  } = overlay;
  const filterCss = resolveImageFilterCss(filter);

  // Check if we have a valid URL (must be http/https, blob, or data URL)
  const isValidUrl = imageUrl && (
    imageUrl.startsWith('http://') ||
    imageUrl.startsWith('https://') ||
    imageUrl.startsWith('blob:') ||
    imageUrl.startsWith('data:')
  );

  if (!isValidUrl && !sourceColor) {
    // Return empty placeholder if URL isn't ready (no visible loading state)
    return null;
  }

  // Calculate dimensions
  const left = (position.x / 100) * containerWidth;
  const top = (position.y / 100) * containerHeight;
  const widthPx = (width / 100) * containerWidth;
  // If height is not specified, we'll let the image determine it based on aspect ratio
  const heightPx = height !== undefined ? (height / 100) * containerHeight : undefined;

  // Scale border radius and determine if it should be an oval
  // If borderRadius is >= half the smaller dimension, use 50% for a true oval/ellipse
  const scaledBorderRadius = borderRadius * scaleFactor;
  const smallerDimension = Math.min(widthPx, heightPx ?? widthPx);
  const isOval = scaledBorderRadius >= smallerDimension / 2;
  const effectiveBorderRadius = isOval ? '50%' : scaledBorderRadius;

  // Build box shadow from shadow config (scale the offsets and blur)
  let boxShadow: string | undefined;
  if (shadow) {
    const offsetX = (shadow.offsetX ?? 0) * scaleFactor;
    const offsetY = (shadow.offsetY ?? 0) * scaleFactor;
    const blur = shadow.blur * scaleFactor;
    boxShadow = `${offsetX}px ${offsetY}px ${blur}px ${shadow.color}`;
  }

  let rotationTransform: string | undefined;
  if (rotation) {
    if (layoutEngine !== 'remotion' && heightPx !== undefined) {
      const rad = (rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const dx = (widthPx / 2) * (cos - 1) - (heightPx / 2) * sin;
      const dy = (widthPx / 2) * sin + (heightPx / 2) * (cos - 1);
      rotationTransform = `translate(${dx}px, ${dy}px) rotate(${rotation}deg)`;
    } else {
      rotationTransform = `rotate(${rotation}deg)`;
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: widthPx,
        height: heightPx,
        transform: rotationTransform,
        transformOrigin: 'center',
        zIndex,
        opacity: opacity / 100,
        overflow: 'hidden',
        borderRadius: effectiveBorderRadius,
        boxShadow,
      }}
    >
      {sourceColor ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: sourceColor,
          }}
        />
      ) : (
        <Img
          src={imageUrl}
          style={{
            width: '100%',
            height: '100%',
            objectFit: objectFit,
            objectPosition: objectFit === 'cover'
              ? `${objectPosition?.x ?? 50}% ${objectPosition?.y ?? 50}%`
              : undefined,
            filter: filterCss,
          }}
        />
      )}
    </div>
  );
};

export default RemotionImageOverlay;
